/**
 * What a Notes thread hangs off. The subject type and ID together name one
 * thread: a Post and a ContentItem can share a raw ID, so neither alone does.
 */
export type NoteSubject =
  | { subjectType: 'post'; subjectId: string }
  | { subjectType: 'contentItem'; subjectId: string }

export type NoteSubjectType = NoteSubject['subjectType']

export type PostNoteSubject = Extract<NoteSubject, { subjectType: 'post' }>
export type ContentItemNoteSubject = Extract<
  NoteSubject,
  { subjectType: 'contentItem' }
>

export const postNoteSubject = (postId: string): PostNoteSubject => ({
  subjectType: 'post',
  subjectId: postId,
})

export const contentItemNoteSubject = (
  contentItemId: string,
): ContentItemNoteSubject => ({
  subjectType: 'contentItem',
  subjectId: contentItemId,
})

/**
 * Rebuilds a subject from its parts, so a hook can depend on the two strings
 * rather than on the identity of the object its caller passed.
 */
export const makeNoteSubject = (
  subjectType: NoteSubjectType,
  subjectId: string,
): NoteSubject =>
  subjectType === 'post'
    ? postNoteSubject(subjectId)
    : contentItemNoteSubject(subjectId)

/**
 * The key for state that belongs to one account's view of one thread, such
 * as an unsent draft. It includes the subject type, so two subjects with the
 * same raw ID never share state.
 */
export const noteSubjectKey = (
  accountId: string | null,
  { subjectType, subjectId }: NoteSubject,
): string => JSON.stringify([accountId, subjectType, subjectId])
